/**
 * Resumable multipart uploader for stream recordings.
 *
 * These files are 5-20 GB and go up over a home connection, so interruption is
 * the expected case, not the exception. The design assumption is that any given
 * attempt will fail partway through and the next one must pick up where it
 * stopped — including after a restart, since part state lives on the server
 * rather than in memory.
 *
 * It lives in this package, beside the session recorder, because two callers
 * now need it: the dashboard, where a streamer picks the file by hand, and
 * Studio, which uploads the recording the moment the session ends. One copy for
 * the same reason `deriveReactions` has one — the retry, resume and ETag rules
 * here are subtle, and a second implementation would drift silently and lose
 * someone's stream.
 *
 * What differs between the two callers is only where the bytes come from, so
 * that is the one thing injected. A browser slices a `File`; Studio reads a
 * range off disk through its Rust side, because the file was written by OBS and
 * the webview has no handle to it. Everything else — which parts are missing,
 * how many run at once, backoff, progress, finalising — is shared.
 */
/** The browser case: a `File` from an input, or any Blob. */
export function blobSource(blob) {
    return {
        size: blob.size,
        read: async (start, end) => blob.slice(start, end),
    };
}
export class UploadAborted extends Error {
    constructor() {
        super("Upload aborted");
        this.name = "UploadAborted";
    }
}
export class RecordingUploader {
    o;
    aborted = false;
    completed = 0;
    bytesDone = 0;
    samples = [];
    fetchImpl;
    concurrency;
    maxAttempts;
    constructor(o) {
        this.o = o;
        // Wrapped, not stored bare: the global fetch must be invoked with the
        // window as its receiver, and this is called as `this.fetchImpl(...)` —
        // which would make the uploader the receiver and throw
        // "Failed to execute 'fetch' on 'Window': Illegal invocation".
        // Tests always inject fetchImpl, so only real uploads ever hit this line.
        this.fetchImpl = o.fetchImpl ?? ((input, init) => fetch(input, init));
        // Four is about where a home upstream saturates. More connections past that
        // point split the same bandwidth and make each part likelier to time out.
        this.concurrency = o.concurrency ?? 4;
        this.maxAttempts = o.maxAttemptsPerPart ?? 5;
    }
    abort() {
        this.aborted = true;
    }
    emit() {
        const now = Date.now();
        this.samples.push({ at: now, bytes: this.bytesDone });
        // Trailing 30s window: long enough to smooth out part boundaries, short
        // enough that the estimate reacts when the connection changes.
        while (this.samples.length > 1 && now - this.samples[0].at > 30_000)
            this.samples.shift();
        const first = this.samples[0];
        const elapsed = (now - first.at) / 1000;
        const rate = elapsed > 1 ? (this.bytesDone - first.bytes) / elapsed : null;
        const remaining = this.o.source.size - this.bytesDone;
        this.o.onProgress?.({
            partsComplete: this.completed,
            partsTotal: this.o.partsTotal,
            bytesUploaded: this.bytesDone,
            bytesTotal: this.o.source.size,
            rate,
            etaSeconds: rate && rate > 0 ? Math.round(remaining / rate) : null,
        });
    }
    range(partNumber) {
        const start = (partNumber - 1) * this.o.partSize;
        return { start, end: Math.min(start + this.o.partSize, this.o.source.size) };
    }
    /**
     * Uploads one part, retrying with backoff.
     *
     * S3 returns the ETag in a response header; it must be sent back verbatim,
     * quotes included, or CompleteMultipartUpload rejects the whole thing.
     *
     * The bytes are read inside the retry loop rather than once above it. A read
     * can fail too — a drive going to sleep, a file moved mid-upload — and a
     * failure that happens on the first attempt and would succeed on the second
     * should not end the upload.
     */
    async uploadPart(part) {
        const { start, end } = this.range(part.number);
        for (let attempt = 1; attempt <= this.maxAttempts; attempt++) {
            if (this.aborted)
                throw new UploadAborted();
            try {
                const body = await this.o.source.read(start, end);
                const res = await this.fetchImpl(part.url, { method: "PUT", body: body });
                if (!res.ok)
                    throw new Error(`PUT part ${part.number}: ${res.status}`);
                const etag = res.headers.get("etag");
                if (!etag)
                    throw new Error(`part ${part.number} returned no ETag`);
                this.bytesDone += end - start;
                this.completed++;
                this.emit();
                return { number: part.number, etag };
            }
            catch (err) {
                if (this.aborted)
                    throw new UploadAborted();
                if (attempt === this.maxAttempts)
                    throw err;
                await new Promise((r) => setTimeout(r, Math.min(1000 * 2 ** attempt, 30_000)));
            }
        }
        throw new Error("unreachable");
    }
    /**
     * Runs the upload to completion, resuming from whatever the server already
     * has. Safe to call again after a failure — it re-asks what is missing rather
     * than assuming anything about previous attempts.
     */
    async run() {
        // A resumed upload starts with bytes already on the server. Count them so
        // the progress bar doesn't restart at zero and imply lost work.
        const initial = await this.o.getMissing(0);
        const alreadyDone = this.o.partsTotal - initial.missing;
        this.completed = alreadyDone;
        this.bytesDone = Math.min(alreadyDone * this.o.partSize, this.o.source.size);
        this.emit();
        while (!this.aborted) {
            const { parts, missing } = await this.o.getMissing(this.concurrency * 4);
            if (missing === 0 || parts.length === 0)
                break;
            // Fixed-size worker pool rather than batched waves: a slow part doesn't
            // stall the others behind a barrier.
            const pending = [...parts];
            const acked = [];
            const worker = async () => {
                while (!this.aborted) {
                    const part = pending.shift();
                    if (!part)
                        return;
                    acked.push(await this.uploadPart(part));
                    // Report in small groups. Reporting every part is chatty; reporting
                    // only at the end means a crash loses the whole batch's progress.
                    if (acked.length >= 4) {
                        await this.o.reportParts(acked.splice(0, acked.length));
                    }
                }
            };
            try {
                await Promise.all(Array.from({ length: Math.min(this.concurrency, pending.length) }, worker));
            }
            finally {
                // Whatever landed before the failure still counts.
                if (acked.length)
                    await this.o.reportParts(acked).catch(() => { });
            }
        }
        if (this.aborted)
            throw new UploadAborted();
        await this.o.finalise();
    }
}
//# sourceMappingURL=uploader.js.map